import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Badge,
  Button,
  ButtonGroup,
  Card,
  Col,
  Nav,
  Form,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useGetLazy } from "../../api";
import { FilterBar, PageHeader } from "../../components/ui";
import { settingsType, useSettings } from "../../hooks";
import { BrandTypeahead } from "../../components/workbench/brand-typeahead";
import { PartnerTypeahead } from "../../components/workbench/partner-typeahead";
import { CouponInsights } from "./coupon-insights";
import { ICouponQuery, queryParams } from "./coupon-query";
import { SyncControl } from "./sync-control";
import { PartnerCell } from "./partner-cell";
import {
  BrandLink,
  CouponSearchLink,
  DealLink,
  searchCountry,
} from "./kindred-links";
import { CouponRawEvents } from "./coupon-raw-events";
import {
  AddToPipelineModal,
  OpenReviews,
  ReviewAlerts,
  useCouponReviews,
} from "./coupon-reviews";
import {
  COUPON_DOMAIN_SOURCE_LABELS,
  COUPON_RARELY_MIN_CHECKED,
  COUPON_NO_MESSAGE as NO_MESSAGE,
  COUPON_WORKING_MIN_RATE,
  ICouponAnalyticsSyncState,
  ICouponPerformanceResponse,
  ICouponPerformanceRow,
  TCouponVerdict,
} from "../../interfaces";

/**
 * Which coupon codes actually work for real users, so dead ones can be
 * retested or deactivated.
 *
 * Keyed on the code, not the merchant, because that is what the data supports:
 * only ~3% of live events carry a URL (Android sends a brand id instead), so a
 * merchant-keyed view would describe a sliver of reality. Every row, by
 * contrast, carries a code.
 */

const VERDICT_META: Record<
  TCouponVerdict,
  { label: string; bg: string; text?: "dark" | "light"; meaning: string }
> = {
  failing: {
    label: "Not working",
    bg: "danger",
    text: "light",
    meaning: "Nobody got a discount out of this code in this window.",
  },
  rarely: {
    label: "Rarely works",
    bg: "warning",
    text: "dark",
    meaning:
      "It worked, but on fewer of the attempts the shop checked than the minimum success rate.",
  },
  working: {
    label: "Working",
    bg: "success",
    text: "light",
    meaning:
      "Real users got a discount out of it at or above the minimum success rate.",
  },
};

const HOUR = 3600 * 1000;
const agoIso = (ms: number) => new Date(Date.now() - ms).toISOString();

const RANGES: Array<{ label: string; ms: number }> = [
  { label: "30m", ms: HOUR / 2 },
  { label: "1h", ms: HOUR },
  { label: "4h", ms: 4 * HOUR },
  { label: "24h", ms: 24 * HOUR },
  { label: "7d", ms: 7 * 24 * HOUR },
  { label: "30d", ms: 30 * 24 * HOUR },
  { label: "90d", ms: 90 * 24 * HOUR },
];

const pct = (n: number | null) =>
  n == null ? "—" : `${(n * 100).toFixed(n > 0 && n < 0.01 ? 2 : 0)}%`;

/** The merchant's site and where it came from, or why there is none. */
const DomainCell: React.FC<{ row: ICouponPerformanceRow }> = ({ row: r }) => {
  if (!r.domain) {
    return r.couponId ? (
      <Link
        to={`/kindred/coupons/${r.couponId}`}
        onClick={(e) => e.stopPropagation()}
        className="text-warning"
        title={`No domain: ${r.domainNote ?? "unknown"}. Open the coupon to add one.`}
      >
        No domain
      </Link>
    ) : (
      <span
        className="text-warning"
        title={`No domain: ${r.domainNote ?? "unknown"}. The code didn't match one of our coupons.`}
      >
        No domain
      </span>
    );
  }
  const label = (
    <>
      {r.domain}
      {r.domainSource && (
        <span className="text-muted">
          {" "}
          · {COUPON_DOMAIN_SOURCE_LABELS[r.domainSource]}
        </span>
      )}
    </>
  );
  return r.checkoutUrl ? (
    <a
      href={r.checkoutUrl}
      target="_blank"
      rel="noreferrer"
      onClick={(e) => e.stopPropagation()}
      title={`Open the checkout page this was seen on: ${r.checkoutUrl}`}
    >
      {label}
    </a>
  ) : (
    <span title="Where this domain came from: the coupon's own domains, its deal's, or the brand's only domain.">
      {label}
    </span>
  );
};

type TTab = "codes" | "insights" | "raw";
const PLATFORMS = ["Android", "IOS", "ChromeDesktop", "Samsung"];
const TABS: TTab[] = ["codes", "insights", "raw"];

/** The two statuses, or neither. */
type TBucket = "" | TCouponVerdict;

const PAGE_SIZE = 50;

/** Server cap on how many codes are ranked, so the export can take the lot. */
const MAX_ROWS = 500;

const Pager: React.FC<{
  page: number;
  limit: number;
  total: number;
  onPage: (p: number) => void;
}> = ({ page, limit, total, onPage }) => {
  const start = total === 0 ? 0 : page * limit + 1;
  const end = Math.min(total, (page + 1) * limit);
  return (
    <div className="d-flex align-items-center justify-content-between px-3 py-2 border-top">
      <small className="text-muted">
        {start}–{end} of {total}
      </small>
      <ButtonGroup size="sm">
        <Button
          variant="outline-secondary"
          disabled={page === 0}
          onClick={() => onPage(page - 1)}
          title="Previous page"
        >
          Previous
        </Button>
        <Button
          variant="outline-secondary"
          disabled={end >= total}
          onClick={() => onPage(page + 1)}
          title="Next page"
        >
          Next
        </Button>
      </ButtonGroup>
    </div>
  );
};

const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString() : "—";

/** The busiest country, with the rest on hover — most codes have one or two. */
const CountryCell: React.FC<{
  countries: Array<{ country: string; attempts: number; applied: number }>;
}> = ({ countries }) => {
  if (!countries.length) return <span className="text-muted">—</span>;
  const [top, ...rest] = countries;
  return (
    <span
      title={countries
        .map((c) => `${c.country}: ${c.attempts} tried, ${c.applied} worked`)
        .join("\n")}
    >
      {top.country}
      {rest.length > 0 && <span className="text-muted"> +{rest.length}</span>}
    </span>
  );
};

const VerdictBadge: React.FC<{ value: TCouponVerdict }> = ({ value }) => {
  const m = VERDICT_META[value];
  return (
    <Badge bg={m.bg} text={m.text} title={m.meaning}>
      {m.label}
    </Badge>
  );
};

interface IBucket {
  key: TBucket;
  label: string;
  bg: string;
  text?: "dark" | "light";
  meaning: string;
}

/** What is working and what is not. The window picker is what "lately" means. */
const BUCKETS: IBucket[] = [
  {
    key: "failing",
    label: "Not working",
    bg: "danger",
    text: "light",
    meaning: VERDICT_META.failing.meaning,
  },
  {
    key: "rarely",
    label: "Rarely works",
    bg: "warning",
    text: "dark",
    meaning: VERDICT_META.rarely.meaning,
  },
  {
    key: "working",
    label: "Working",
    bg: "success",
    text: "light",
    meaning: VERDICT_META.working.meaning,
  },
];

/** A count with the sentence that says what to do about it. */
const BucketTile: React.FC<{
  bucket: IBucket;
  /** null while the first fetch is in flight — the tile still selects. */
  count: number | null;
  active: boolean;
  onClick: () => void;
}> = ({ bucket: m, count, active, onClick }) => {
  return (
    <Card
      className={`h-100 ${active ? "border-dark border-2" : ""}`}
      style={{ cursor: "pointer" }}
      onClick={onClick}
      title={m.meaning}
    >
      <Card.Body className="py-2 px-3">
        <div className="d-flex align-items-center gap-2">
          <Badge bg={m.bg} text={m.text}>
            &nbsp;
          </Badge>
          <span className="small fw-semibold">{m.label}</span>
        </div>
        <div className="fs-4 fw-bold">
          {count == null ? (
            <span className="text-muted">—</span>
          ) : (
            count.toLocaleString()
          )}
        </div>
      </Card.Body>
    </Card>
  );
};

export const CouponPerformance: React.FC = () => {
  // The chosen range is held as a label, not as the ISO string it produces:
  // comparing `from` to a freshly computed `agoIso(ms)` never matched, because
  // the right-hand side moves every render, so no button ever looked selected.
  const [range, setRange] = useState("24h");
  const [from, setFrom] = useState(agoIso(24 * HOUR));
  const [to, setTo] = useState("");
  const [bucket, setBucket] = useState<TBucket>("");
  const [brandId, setBrandId] = useState("");
  const [brandName, setBrandName] = useState("");
  const [liveOnly, setLiveOnly] = useState(true);
  // One person hitting a dead code is a real person hitting a dead code, so the
  // default is to show it. Raise this to cut the list down to the busiest.
  const [minAttempts, setMinAttempts] = useState(1);
  const [domain, setDomain] = useState("");
  const [noDomainOnly, setNoDomainOnly] = useState(false);
  const [country, setCountry] = useState("");
  const [code, setCode] = useState("");
  const [applied, setApplied] = useState(0);
  const [page, setPage] = useState(0);
  const [partnerId, setPartnerId] = useState("");
  const [minRatePct, setMinRatePct] = useState(COUPON_WORKING_MIN_RATE * 100);
  const [platform, setPlatform] = useState("");
  const [rawStatus, setRawStatus] = useState<ICouponQuery["status"]>("");
  const [rawError, setRawError] = useState("");
  const [rawEventId, setRawEventId] = useState("");
  const [sync, setSync] = useState<ICouponAnalyticsSyncState | null>(null);
  // What the last Search asked the server for; null until the first Search.
  const [query, setQuery] = useState<ICouponQuery | null>(null);
  // The window behind the last Search, so a refresh can move it up to now.
  const queryRangeMs = useRef<number | null>(null);
  const navigate = useNavigate();
  const reviews = useCouponReviews();
  const reloadReviews = reviews.reload;
  const [autoRefresh, setAutoRefresh] = useSettings(
    settingsType.COUPON_PERFORMANCE_AUTO_REFRESH
  );

  // In the URL so a refresh lands on the same tab — still empty until Search.
  const [params, setParams] = useSearchParams();
  const tab: TTab = TABS.includes(params.get("tab") as TTab)
    ? (params.get("tab") as TTab)
    : "codes";
  const setTab = (t: TTab) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (t === "codes") next.delete("tab");
        else next.set("tab", t);
        return next;
      },
      { replace: true }
    );

  const [partnerLabel, setPartnerLabel] = useState("");

  /**
   * The whole ranked set in one request, then every narrowing happens here.
   *
   * The query behind this takes seconds, so sending one per chip click made
   * picking a category a wait rather than a glance. Only the window and the
   * rejection threshold change what the server has to compute; everything else
   * is a view of rows already in hand.
   */
  const { execute, data, loading, error } =
    useGetLazy<ICouponPerformanceResponse>();
  const codesFetchedFor = useRef<ICouponQuery | null>(null);

  // Nothing is fetched on landing; the queries are slow, so they wait for Search.
  useEffect(() => {
    if (!applied) return;
    queryRangeMs.current = to
      ? null
      : (RANGES.find((r) => r.label === range)?.ms ?? null);
    setQuery({
      from,
      to,
      partnerId,
      partnerName: partnerLabel,
      minRate: minRatePct / 100,
      code,
      brandId,
      country,
      platform,
      status: rawStatus,
      error: rawError,
      eventId: rawEventId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applied]);

  // Each tab fetches for the latest Search the first time it is shown.
  useEffect(() => {
    if (tab !== "codes" || !query || codesFetchedFor.current === query) return;
    codesFetchedFor.current = query;
    const p = queryParams(query, "codes");
    p.set("sort", "impact");
    p.set("limit", String(MAX_ROWS));
    execute(`/coupon-performance?${p.toString()}`).catch(() => null);
  }, [tab, query, execute]);

  // Code-level narrowing on rows already in hand; the shared filters ran server-side.
  const filtered = useMemo(() => {
    const needleDomain = domain.trim().toLowerCase();
    return (data?.items ?? []).filter((r) => {
      if (liveOnly && !r.isLive) return false;
      if (r.attempts < minAttempts) return false;
      if (bucket && r.verdict !== bucket) return false;
      if (needleDomain && !(r.domain ?? "").includes(needleDomain))
        return false;
      if (noDomainOnly && r.domain) return false;
      return true;
    });
  }, [data, bucket, liveOnly, domain, noDomainOnly, minAttempts]);

  const counts = useMemo(() => {
    const pool = (data?.items ?? []).filter(
      (r) => (!liveOnly || r.isLive) && r.attempts >= minAttempts
    );
    return {
      "": pool.length,
      failing: pool.filter((r) => r.verdict === "failing").length,
      rarely: pool.filter((r) => r.verdict === "rarely").length,
      working: pool.filter((r) => r.verdict === "working").length,
    } as Record<TBucket, number>;
  }, [data, liveOnly, minAttempts]);

  const rows = useMemo(
    () => filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE),
    [filtered, page]
  );

  // Any narrowing can shrink the list under your feet; staying on page 6 of a
  // now two-page result shows an empty table.
  useEffect(() => {
    setPage(0);
  }, [bucket, liveOnly, domain, noDomainOnly, minAttempts]);

  // Search resets the page itself; a refresh keeps it unless the list got shorter.
  useEffect(() => {
    if (page > 0 && page * PAGE_SIZE >= filtered.length)
      setPage(Math.max(0, Math.ceil(filtered.length / PAGE_SIZE) - 1));
  }, [filtered, page]);

  const noDomainCount = useMemo(
    () =>
      (data?.items ?? []).filter(
        (r) => !r.domain && (!liveOnly || r.isLive) && r.attempts >= minAttempts
      ).length,
    [data, liveOnly, minAttempts]
  );

  const search = () => {
    setPage(0);
    setApplied((n) => n + 1);
  };

  // Re-runs the last Search as it was asked, not the filter bar's unsaved edits.
  const refresh = useCallback(() => {
    const ms = queryRangeMs.current;
    setQuery(
      (q) => q && { ...q, from: ms ? agoIso(ms) : q.from, refreshed: true }
    );
    reloadReviews();
  }, [reloadReviews]);

  // Every row matching the filters, not the page in view — the whole set is
  // already here, so there is nothing to fetch.
  const exportCsv = () => {
    const all = filtered;
    const head = [
      "code",
      "brand",
      "brand id",
      "deal id",
      "domain",
      "domain source",
      "why no domain",
      "markets",
      "countries",
      "partners",
      "checkout url",
      "verdict",
      "attempts",
      "worked",
      "still shown to users",
      "we last tested",
      "what the shop said",
    ];
    const body = all.map((r) =>
      [
        r.code,
        r.brandName ?? "",
        r.brandId ?? "",
        r.campaignId ?? "",
        r.domain ?? "",
        r.domainSource ?? "",
        r.domain ? "" : (r.domainNote ?? ""),
        r.markets.join(" "),
        r.countries.map((c) => `${c.country}:${c.attempts}`).join(" "),
        r.partners
          .map((p) => `${p.name ?? p.partnerId}:${p.attempts}`)
          .join(" | "),
        r.checkoutUrl ?? "",
        VERDICT_META[r.verdict].label,
        r.attempts,
        r.applied,
        r.isLive ? "yes" : "no",
        r.lastTestedAt ?? "never",
        (r.lastError ?? "").replace(/\s+/g, " ").trim(),
      ]
        .map((v) => `"${String(v).replace(/"/g, '""')}"`)
        .join(",")
    );
    const blob = new Blob([[head.join(","), ...body].join("\n")], {
      type: "text/csv",
    });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `coupon-performance-${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;
    a.click();
  };

  return (
    <div className="container-fluid py-3">
      <PageHeader
        title="Coupon Performance"
        description="Which codes actually work for real extension users, so dead ones can be retested or deactivated."
        actions={
          <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end">
            <SyncControl
              onState={setSync}
              onSynced={refresh}
              autoRefresh={autoRefresh !== false}
              onAutoRefreshChange={setAutoRefresh}
            />
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={exportCsv}
              disabled={!filtered.length}
              title="Download every code matching these filters as CSV — not just this page"
            >
              Export CSV ({filtered.length})
            </Button>
          </div>
        }
      />

      {sync && !sync.syncedThrough && sync.status !== "running" && (
        <Alert variant="warning" className="small">
          <strong>No data copied yet.</strong> Every tab reads a copy of the
          partners-analytics CouponAnalytics table kept in our database. Press
          Sync to fill it — the first run copies every row and takes a few
          minutes.
        </Alert>
      )}

      {error && (
        <Alert variant="danger" className="small">
          Could not load codes: {error.message}
        </Alert>
      )}

      <FilterBar
        onSearch={search}
        onClear={() => {
          setRange("24h");
          setFrom(agoIso(24 * HOUR));
          setTo("");
          setPartnerId("");
          setPartnerLabel("");
          setCode("");
          setBrandId("");
          setBrandName("");
          setCountry("");
          setPlatform("");
          setMinRatePct(COUPON_WORKING_MIN_RATE * 100);
          setBucket("");
          setLiveOnly(true);
          setMinAttempts(1);
          setDomain("");
          setNoDomainOnly(false);
          setRawStatus("");
          setRawError("");
          setRawEventId("");
          search();
        }}
        activeCount={
          [partnerId, code, brandId, country, platform].filter(Boolean).length +
          (tab === "codes"
            ? [bucket, domain, noDomainOnly, liveOnly].filter(Boolean).length
            : 0) +
          (tab === "raw"
            ? [rawStatus, rawError, rawEventId].filter(Boolean).length
            : 0)
        }
        summary={
          query
            ? [
                query.partnerId
                  ? `Partner: ${query.partnerName || query.partnerId}`
                  : "All partners",
                tab === "codes" &&
                  data &&
                  `${filtered.length.toLocaleString()} of ${data.total.toLocaleString()} codes`,
                `${fmtDate(query.from)} – ${fmtDate(query.to || null)}`,
              ]
                .filter(Boolean)
                .join(" · ")
            : undefined
        }
      >
        <div className="small text-muted mb-1">
          Every tab · applies on Search
        </div>
        <Row className="g-2">
          <Col md={3}>
            <PartnerTypeahead
              partnerId={partnerId}
              onChange={(id, name) => {
                setPartnerId(id);
                setPartnerLabel(name);
              }}
            />
          </Col>
          <Col md={4}>
            <Form.Label className="small mb-1">Window</Form.Label>
            <div className="d-flex flex-wrap gap-1">
              {RANGES.map((r) => (
                <Button
                  key={r.label}
                  size="sm"
                  variant={range === r.label ? "primary" : "outline-secondary"}
                  onClick={() => {
                    setRange(r.label);
                    setFrom(agoIso(r.ms));
                    setTo("");
                  }}
                  title={`Look at the last ${r.label}`}
                >
                  {r.label}
                </Button>
              ))}
            </div>
          </Col>
          <Col md={2}>
            <Form.Label className="small mb-1">Code</Form.Label>
            <Form.Control
              size="sm"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="exact code"
              title="One coupon code, any case"
            />
          </Col>
          <Col md={4}>
            <BrandTypeahead
              clearable
              brandId={brandId}
              brandName={brandName}
              onChange={(id, name) => {
                setBrandId(id);
                setBrandName(name);
              }}
            />
          </Col>
          <Col md={2}>
            <Form.Label className="small mb-1">Shopper country</Form.Label>
            <Form.Control
              size="sm"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              placeholder="e.g. GB"
              title="Only attempts by shoppers in this country — their location, not the merchant's market"
            />
          </Col>
          <Col md={2}>
            <Form.Label className="small mb-1">Platform</Form.Label>
            <Form.Select
              size="sm"
              value={platform}
              onChange={(e) => setPlatform(e.target.value)}
              title="Which client sent the attempts"
            >
              <option value="">Any</option>
              {PLATFORMS.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col md={2}>
            <Form.Label className="small mb-1">Working means ≥</Form.Label>
            <div className="input-group input-group-sm">
              <Form.Control
                type="number"
                min={0}
                max={100}
                step={1}
                value={minRatePct}
                onChange={(e) =>
                  setMinRatePct(
                    Math.min(100, Math.max(0, Number(e.target.value) || 0))
                  )
                }
                title={`A code that worked on fewer than this share of the attempts the shop checked is "Rarely works" — once it has at least ${COUPON_RARELY_MIN_CHECKED} checked attempts.`}
              />
              <span className="input-group-text">%</span>
            </div>
          </Col>
        </Row>

        {tab === "codes" && (
          <>
            <div className="small text-muted mt-3 mb-1">
              Codes tab only · applies straight away
            </div>
            <Row className="g-2">
              <Col md={2}>
                <Form.Label className="small mb-1">Status</Form.Label>
                <Form.Select
                  size="sm"
                  value={bucket}
                  onChange={(e) => setBucket(e.target.value as TBucket)}
                  title="Narrow to one verdict"
                >
                  <option value="">Any status</option>
                  {BUCKETS.map((b) => (
                    <option key={b.key} value={b.key}>
                      {b.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={2}>
                <Form.Label className="small mb-1">Domain</Form.Label>
                <Form.Control
                  size="sm"
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  placeholder="e.g. lowes.com"
                  title="The site we resolved for the code"
                />
              </Col>
              <Col md={2}>
                <Form.Label className="small mb-1">Min attempts</Form.Label>
                <Form.Control
                  size="sm"
                  type="number"
                  min={1}
                  value={minAttempts}
                  onChange={(e) => setMinAttempts(Number(e.target.value) || 1)}
                  title="Hide codes tried fewer than this many times. Nothing more — it does not change any verdict."
                />
              </Col>
              <Col md={3} className="d-flex align-items-end">
                <Form.Check
                  type="switch"
                  checked={liveOnly}
                  onChange={(e) => setLiveOnly(e.target.checked)}
                  label="Only codes we still serve"
                  title="Hide codes already deactivated or expired — there is nothing to do about those"
                />
              </Col>
              <Col md={3} className="d-flex align-items-end">
                <Form.Check
                  type="switch"
                  checked={noDomainOnly}
                  onChange={(e) => setNoDomainOnly(e.target.checked)}
                  label={`Only codes with no domain${data ? ` (${noDomainCount})` : ""}`}
                  title="Codes we can't tie to one site. They can't go on the Work Pipeline until the coupon's domain is fixed."
                />
              </Col>
            </Row>
          </>
        )}

        {tab === "raw" && (
          <>
            <div className="small text-muted mt-3 mb-1">
              Raw events tab only · applies on Search
            </div>
            <Row className="g-2">
              <Col md={2}>
                <Form.Label className="small mb-1">Result</Form.Label>
                <Form.Select
                  size="sm"
                  value={rawStatus}
                  onChange={(e) =>
                    setRawStatus(e.target.value as ICouponQuery["status"])
                  }
                  title="What the extension reported for each attempt"
                >
                  <option value="">Any</option>
                  <option value="Applied">Applied</option>
                  <option value="Failed">Failed</option>
                </Form.Select>
              </Col>
              <Col md={5}>
                <Form.Label className="small mb-1">Shop message</Form.Label>
                <Form.Control
                  size="sm"
                  value={rawError}
                  onChange={(e) => setRawError(e.target.value)}
                  placeholder="exact message, e.g. Coupon box not found"
                  title={`Only failures with exactly this message. "${NO_MESSAGE}" matches failures that sent none.`}
                />
              </Col>
              <Col md={3}>
                <Form.Label className="small mb-1">Checkout</Form.Label>
                <Form.Control
                  size="sm"
                  value={rawEventId}
                  onChange={(e) => setRawEventId(e.target.value.trim())}
                  placeholder="event id"
                  title="Every code tried in one checkout. Click a checkout id in the table to fill this."
                />
              </Col>
            </Row>
          </>
        )}
      </FilterBar>

      <ReviewAlerts reviews={reviews} />
      <OpenReviews reviews={reviews} />
      <AddToPipelineModal reviews={reviews} />

      <Nav
        variant="tabs"
        className="mb-3"
        activeKey={tab}
        onSelect={(k) => setTab((k as TTab) ?? "codes")}
      >
        <Nav.Item>
          <Nav.Link
            eventKey="codes"
            title="The worklist: every code, newest problems first"
          >
            Codes
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link
            eventKey="insights"
            title="Success per checkout, per attempt and per code, and where the failures are"
          >
            Insights
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link
            eventKey="raw"
            title="The rows exactly as the extension sent them"
          >
            Raw events
          </Nav.Link>
        </Nav.Item>
      </Nav>

      {/* Kept mounted so switching back does not refetch or lose paging. */}
      <div className={tab === "insights" ? "" : "d-none"}>
        <CouponInsights
          query={query}
          active={tab === "insights"}
          onPartner={(id, name) => {
            setPartnerId(id);
            setPartnerLabel(name);
            search();
          }}
          onBrand={(id, name) => {
            setBrandId(id);
            setBrandName(name);
            setTab("codes");
            search();
          }}
          onError={(message) => {
            setRawError(message);
            setRawStatus("");
            setRawEventId("");
            setTab("raw");
            search();
          }}
        />
      </div>
      <div className={tab === "raw" ? "" : "d-none"}>
        <CouponRawEvents
          query={query}
          active={tab === "raw"}
          onCheckout={(eventId) => {
            setRawEventId(eventId);
            search();
          }}
          reviews={reviews}
        />
      </div>

      {tab === "codes" && (
        <>
          <Row className="g-2 mb-3">
            {BUCKETS.map((b) => (
              <Col md={3} key={b.key || "all"}>
                <BucketTile
                  bucket={b}
                  count={data ? (counts[b.key] ?? 0) : null}
                  active={bucket === b.key}
                  onClick={() => setBucket(bucket === b.key ? "" : b.key)}
                />
              </Col>
            ))}
          </Row>

          {data && (
            <>
              <Alert variant="light" className="small border py-2">
                {data.coverage.distinctCodes.toLocaleString()} codes seen across{" "}
                {data.coverage.events.toLocaleString()} real checkouts in this
                window, busiest first (top {MAX_ROWS}).{" "}
                <strong>
                  A code is only judged on attempts the shop actually checked
                </strong>{" "}
                — attempts where we could not find the discount box are counted
                separately, so a broken template of ours never switches off a
                good coupon.
              </Alert>
            </>
          )}

          {loading && data && (
            <small className="text-muted d-block mb-1">
              <Spinner size="sm" className="me-1" /> Refreshing…
            </small>
          )}
          <Card>
            <Card.Body className="p-0">
              {loading && !data ? (
                <div className="text-center py-4">
                  <Spinner />
                </div>
              ) : (
                <Table hover responsive className="mb-0 align-middle">
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th title="Hover a brand for its brand and deal ids — both are columns in the CSV export">
                        Brand
                      </th>
                      <th title="The deal the code ran under, first four characters. Hover for the full id.">
                        Deal
                      </th>
                      <th title="The merchant's site: the checkout URL a shopper reported, else the coupon's, deal's or brand's own domain. 'No domain' means none of those names exactly one site.">
                        Domain
                      </th>
                      <th title="The markets the coupon is enabled in. Issues are raised for the shopper's country; these are used only when analytics has none.">
                        Market
                      </th>
                      <th title="Where the shoppers trying this code were, busiest first. This is their own location, not the merchant's market.">
                        Shopper country
                      </th>
                      <th title="Which partner apps the attempts came from, busiest first. Hover for the split.">
                        Partner
                      </th>
                      <th>Status</th>
                      <th className="text-end">Attempts</th>
                      <th className="text-end">Worked</th>
                      <th
                        className="text-end"
                        title="Worked ÷ attempts the shop actually checked. Attempts where we never found the coupon box are left out."
                      >
                        Success
                      </th>
                      <th>Still shown</th>
                      <th>We last tested</th>
                      <th>What the shop said</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr
                        key={`${r.code}-${r.brandId ?? "none"}`}
                        style={{ cursor: "pointer" }}
                        title="Open this code's history and charts"
                        onClick={() =>
                          navigate(
                            `/coupon-performance/${encodeURIComponent(r.code)}?` +
                              new URLSearchParams({
                                ...(r.brandId ? { brandId: r.brandId } : {}),
                                ...(query?.partnerId
                                  ? { partnerId: query.partnerId }
                                  : {}),
                                minRate: String(
                                  query?.minRate ?? COUPON_WORKING_MIN_RATE
                                ),
                                range,
                              }).toString()
                          )
                        }
                      >
                        <td className="small text-nowrap">
                          <span className="font-monospace">{r.code}</span>
                          <CouponSearchLink
                            code={r.code}
                            brandId={r.brandId}
                            country={searchCountry(
                              r.countries.map((c) => c.country),
                              r.markets
                            )}
                          />
                        </td>
                        <td
                          className="small"
                          title={`Brand ID: ${r.brandId ?? "—"}\nDeal ID: ${
                            r.campaignId ?? "—"
                          }`}
                        >
                          {r.brandName ?? "—"}
                          <BrandLink brandId={r.brandId} />
                        </td>
                        <td className="small">
                          <DealLink campaignId={r.campaignId} />
                        </td>
                        <td className="small">
                          <DomainCell row={r} />
                        </td>
                        <td className="small">
                          {r.markets.length ? (
                            r.markets.join(", ")
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td className="small">
                          <CountryCell countries={r.countries} />
                        </td>
                        <td className="small">
                          <PartnerCell partners={r.partners} />
                        </td>
                        <td>
                          <VerdictBadge value={r.verdict} />
                        </td>
                        <td className="text-end">
                          {r.attempts.toLocaleString()}
                        </td>
                        <td className="text-end">
                          {r.applied.toLocaleString()}
                        </td>
                        <td
                          className="text-end"
                          title={`${r.applied} of ${r.attempts - r.templateBlocked} attempts the shop checked; ${r.templateBlocked} never found the coupon box`}
                        >
                          {pct(r.applyRate)}
                        </td>
                        <td>
                          {r.isLive ? (
                            <Badge bg="danger">yes</Badge>
                          ) : (
                            <span className="text-muted small">no</span>
                          )}
                        </td>
                        <td className="small text-muted text-nowrap">
                          {r.lastTestedAt ? fmtDate(r.lastTestedAt) : "never"}
                        </td>
                        <td
                          className="small text-muted text-truncate"
                          style={{ maxWidth: 240 }}
                          title={r.lastError ?? ""}
                        >
                          {r.lastError?.trim() || "—"}
                        </td>
                      </tr>
                    ))}
                    {!rows.length && !loading && (
                      <tr>
                        <td
                          colSpan={14}
                          className="text-center text-muted py-4"
                        >
                          {data
                            ? "No coupon activity matches this filter."
                            : "Pick a window and press Search."}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              )}
              {!loading && filtered.length > PAGE_SIZE && (
                <Pager
                  page={page}
                  limit={PAGE_SIZE}
                  total={filtered.length}
                  onPage={setPage}
                />
              )}
            </Card.Body>
          </Card>
        </>
      )}
    </div>
  );
};
